import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ResearchStudy {
  title: string;
  institution: string;
  year: string;
  finding: string;
  impact: string;
}

interface ResearchSectionProps {
  studies: ResearchStudy[];
}

const ResearchSection: React.FC<ResearchSectionProps> = ({ studies }) => {
  return (
    <section className="py-16 bg-card">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Research & Evidence
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Academic backing and psychological research supporting the power of forgiveness
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {studies.map((study, index) => (
            <div
              key={index}
              className="bg-background p-8 rounded-xl shadow-md border border-border hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="AcademicCapIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                    {study.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {study.institution} • {study.year}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-cta font-bold text-foreground uppercase mb-2">Key Finding:</p>
                  <p className="text-foreground font-body leading-relaxed">{study.finding}</p>
                </div>
                <div>
                  <p className="text-sm font-cta font-bold text-foreground uppercase mb-2">Impact:</p>
                  <p className="text-muted-foreground font-body leading-relaxed">{study.impact}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-12 rounded-2xl border border-border">
          <div className="grid lg:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
                <Icon name="HeartIcon" size={32} className="text-success" />
              </div>
              <p className="text-4xl font-headline font-bold text-foreground mb-2">78%</p>
              <p className="text-muted-foreground font-body">Reduction in stress levels after forgiveness practice</p>
            </div>
            <div>
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                <Icon name="UserGroupIcon" size={32} className="text-accent-foreground" />
              </div>
              <p className="text-4xl font-headline font-bold text-foreground mb-2">65%</p>
              <p className="text-muted-foreground font-body">Improvement in relationship quality</p>
            </div>
            <div>
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                <Icon name="SparklesIcon" size={32} className="text-secondary" />
              </div>
              <p className="text-4xl font-headline font-bold text-foreground mb-2">82%</p>
              <p className="text-muted-foreground font-body">Participants report lasting positive change</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResearchSection;